import React, { useState, useEffect } from "react";
import { Box, Grid, TextField, Button, Typography, Chip } from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import {
  createCountryRequest,
  updateCountryRequest,
} from "../../../redux/CountryList/CountrySlice";
import { toast } from "react-toastify";

const CreateOrEditCountryContent = ({ initialData, onClose }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.country || {});

  const [form, setForm] = useState({
    countryName: "",
    countryCode: "",
    dialCode: "",
    phoneLength: "",
    currency: "",
    currencySymbol: "",
  });

  const [stateInput, setStateInput] = useState(""); // new state input
  const [statesArray, setStatesArray] = useState([]); // temporary states array

  useEffect(() => {
    if (initialData) {
      setForm({
        countryName: initialData.countryName || "",
        countryCode: initialData.countryCode || "",
        dialCode: initialData.dialCode || "",
        phoneLength: initialData.phoneLength || "",
        currency: initialData.currency || "",
        currencySymbol: initialData.currencySymbol || "",
      });
      setStatesArray(initialData.states || []); // load existing states if any
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddState = () => {
    const trimmedState = stateInput.trim();
    if (!trimmedState) return;
    if (statesArray.includes(trimmedState)) {
      toast.error("State already added");
      return;
    }
    setStatesArray((prev) => [...prev, trimmedState]);
    setStateInput("");
  };

  const handleRemoveState = (stateToRemove) => {
    setStatesArray((prev) => prev.filter((s) => s !== stateToRemove));
  };

  const handleSave = () => {
    if (!form.countryName) return toast.error("Country Name is required.");
    if (!form.countryCode) return toast.error("Country Code is required.");
    if (!form.dialCode) return toast.error("DialCode is required.");
    if (!form.currency) return toast.error("Currency is required.");
    if (!form.currencySymbol)
      return toast.error("Currency Symbol is required.");
    if (!form.phoneLength) return toast.error("Phone Length is required.");

    const phoneLengthNumber = Number(form.phoneLength);
    if (isNaN(phoneLengthNumber) || phoneLengthNumber <= 0) {
      return toast.error("Enter valid Phone Length.");
    }

    // include statesArray in the form data
    const finalForm = { ...form, states: statesArray };

    if (initialData?._id) {
      dispatch(updateCountryRequest({ _id: initialData._id, ...finalForm }));
    } else {
      dispatch(createCountryRequest(finalForm));
    }
    onClose();
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
          color: "#fff",
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography fontWeight={700} fontSize={24} fontFamily={"Aeonik"}>
          {initialData ? "Edit Country" : "Create Country"}
        </Typography>
        <Box
          onClick={onClose}
          sx={{
            cursor: "pointer",
            background: "#E2E2EA",
            color: "#646464",
            borderRadius: "50%",
            width: 26,
            height: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RxCross2 />
        </Box>
      </Box>

      {/* Form */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              sx={{ width: "370px" }}
              name="countryName"
              label="Country Name"
              placeholder="Enter Country Name"
              size="small"
              value={form.countryName}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              sx={{ width: "370px" }}
              name="countryCode"
              label="Country Code"
              placeholder="Enter Country Code"
              size="small"
              value={form.countryCode}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              sx={{ width: "370px" }}
              name="dialCode"
              label="Dial Code"
              placeholder="Enter Dial Code"
              size="small"
              value={form.dialCode}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              sx={{ width: "370px" }}
              name="phoneLength"
              label="Phone Length"
              type="number"
              placeholder="Enter Phone Length"
              size="small"
              value={form.phoneLength}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              sx={{ width: "370px" }}
              name="currency"
              label="Currency"
              placeholder="Enter Currency"
              size="small"
              value={form.currency}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              sx={{ width: "370px" }}
              name="currencySymbol"
              label="Currency Symbol"
              placeholder="Enter Currency Symbol"
              size="small"
              value={form.currencySymbol}
              onChange={handleChange}
            />
          </Grid>
        </Grid>

        {/* States input */}
        <Grid container spacing={2} sx={{ mt: 2 }} alignItems="center">
          <Grid item xs={8}>
            <TextField
              fullWidth
              sx={{ width: "370px" }}
              label="Add State"
              placeholder="Enter state"
              size="small"
              value={stateInput}
              onChange={(e) => setStateInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddState();
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Button
              variant="contained"
              sx={{ bgcolor: "#752ADA", textTransform: "none" }}
              onClick={handleAddState}
            >
              Add
            </Button>
          </Grid>

          {/* Show added states */}
          <Grid item xs={12}>
            {statesArray.map((state) => (
              <Chip
                key={state}
                label={state}
                onDelete={() => handleRemoveState(state)}
                sx={{ mr: 1, mt: 1 }}
              />
            ))}
          </Grid>
        </Grid>

        <Button
          variant="contained"
          sx={{ bgcolor: "#752ADA", px: 6, textTransform: "none", mt: 3 }}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateOrEditCountryContent;
